export type DropCallback = (x: number) => void

export class InputHandler {
  private canvas: HTMLCanvasElement
  private currentX: number | null = null
  private dropCallback: DropCallback | null = null
  private enabled: boolean = true

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    // Mouse events
    this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this))
    this.canvas.addEventListener('click', this.handleClick.bind(this))
    this.canvas.addEventListener('mouseleave', this.handleMouseLeave.bind(this))

    // Touch events
    this.canvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false })
    this.canvas.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false })
    this.canvas.addEventListener('touchend', this.handleTouchEnd.bind(this))
  }

  private getCanvasX(clientX: number): number {
    const rect = this.canvas.getBoundingClientRect()
    return clientX - rect.left
  }

  private handleMouseMove(e: MouseEvent): void {
    this.currentX = this.getCanvasX(e.clientX)
  }

  private handleClick(e: MouseEvent): void {
    if (!this.enabled) return
    const x = this.getCanvasX(e.clientX)
    this.triggerDrop(x)
  }

  private handleMouseLeave(): void {
    this.currentX = null
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault()
    if (e.touches.length > 0) {
      this.currentX = this.getCanvasX(e.touches[0].clientX)
    }
  }

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault()
    if (e.touches.length > 0) {
      this.currentX = this.getCanvasX(e.touches[0].clientX)
    }
  }

  private handleTouchEnd(_e: TouchEvent): void {
    if (!this.enabled) return
    if (this.currentX !== null) {
      this.triggerDrop(this.currentX)
    }
  }

  private triggerDrop(x: number): void {
    if (this.dropCallback) {
      this.dropCallback(x)
    }
  }

  onDrop(callback: DropCallback): void {
    this.dropCallback = callback
  }

  getX(): number | null {
    return this.currentX
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  clampX(x: number, margin: number): number {
    return Math.max(margin, Math.min(this.canvas.getBoundingClientRect().width - margin, x))
  }
}
